import React, {useState, useRef, useEffect} from 'react';
import './Franchise.css';
import Footer from '../Footer';
import FloatingOrderButton from '../../components/FloatingOrderButton';
import { FaHandshake, FaCheckCircle, FaTools, FaUsersCog, FaMoneyBillWave, FaSignInAlt } from 'react-icons/fa';
import api from '../api';
import SupportSection from '../home/Support';
import SetupInsights from './SetUpInsights';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box} from '@mui/material';
import { useAppSelector } from '../../store/hooks';

export default function Franchise() {

  const formRef = useRef(null);
  const navigate = useNavigate();
  const stats = useAppSelector((state) => state.stats);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const location = useLocation();

  useEffect(() => {
    if (location.hash === '#form') {
      const el = document.getElementById('franchise-form');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location]);

  return (
    <div className="franchise-page">
      <div className="franchise-hero">
        <h1>Join NeatnClean Laundry Franchise</h1>
        <p>Be a part of India's fastest growing dry-cleaning brand. Low investment, high return, full support.</p>
        
        <div className="franchise-hero-actions">
          <button className="hero-btn primary" onClick={scrollToForm}>
            <FaHandshake />
            Apply for Franchise
          </button>
          <button 
            className="hero-btn secondary" 
            onClick={() => navigate('/franchise/login')}
          >
            <FaSignInAlt />
            Franchise Login
          </button>
        </div>
      </div>

      <div className="franchise-content">
        <FranchiseTimelineFeature></FranchiseTimelineFeature>

        <FranchiseForm refProp={formRef}></FranchiseForm>

        <SupportSection></SupportSection>

        <SetupInsights></SetupInsights>

        <section className="franchise-extra">
          <div className="wave-bg"></div>
          <div className="franchise-extra-container">
            <h2>Why Choose NeatnClean?</h2>
            <ul>
              <li><span className="check-icon"></span> Proven business model with several outlets</li>
              <li><span className="check-icon"></span> No prior experience required</li>
              <li><span className="check-icon"></span> Comprehensive setup & launch support</li>
              <li><span className="check-icon"></span> Centralized marketing campaigns</li>
              <li><span className="check-icon"></span> Transparent revenue sharing</li>
              <li><span className="check-icon"></span> Attractive ROI within 12–18 months</li>
            </ul>
          </div>
        </section>

        <section className="franchise-impact">
          <h2>Our Impact</h2>
          <div className="impact-stats">
            <div className="impact-card">
              <h3>{stats.storeCount}</h3>
              <p>Operational Stores</p>
            </div>
            <div className="impact-card">
              <h3>{stats.storeCount}</h3>
              <p>Service Areas</p>
            </div>
            <div className="impact-card">
              <h3>{stats.customerCount.toLocaleString()}+</h3>
              <p>Customers Served</p>
            </div>
            <div className="impact-card">
              <h3>24 Hrs</h3>
              <p>Avg. Delivery Time</p>
            </div>
          </div>

          <div className="franchise-cta">
            <p>Ready to launch your franchise?</p>
            <button className="cta-button" onClick={scrollToForm}>Get Started Now</button>
          </div>
        </section>

        <FranchiseFaqs />
      </div>
      <Footer />
      <FloatingOrderButton />
    </div>
  );
}

function Feature({ icon, title, desc }) {
  return (
    <div className="feature-item">
      <div className="feature-icon">{icon}</div>
      <div>
        <h3>{title}</h3>
        <p>{desc}</p>
      </div>
    </div>
  );
}

function FranchiseFaqs() {
  const [openIndex, setOpenIndex] = useState(null);
  const faqs = [
    {
      q: 'How much investment is required to start?',
      a: 'Investment varies by city and store size. Our team shares a transparent breakup covering setup, equipment, and launch support.'
    },
    {
      q: 'Do I need prior experience in laundry?',
      a: 'No. We provide complete training for store operations, quality control, customer handling, and business tools.'
    },
    {
      q: 'How long does it take to go live?',
      a: 'Typically 30–45 days from agreement to launch, including interiors, equipment installation, hiring, and training.'
    },
    {
      q: 'What support do you provide after launch?',
      a: 'Centralized marketing, performance reviews, operations playbooks, vendor partnerships, and priority support.'
    }
  ];

  const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

  return (
    <section className="franchise-faqs">
      <h2>Franchise FAQs</h2>
      <div className="faq-list" role="list">
        {faqs.map((item, i) => (
          <div className={`faq-item ${openIndex === i ? 'open' : ''}`} key={i} role="listitem">
            <button
              className="faq-question"
              aria-expanded={openIndex === i}
              aria-controls={`faq-panel-${i}`}
              onClick={() => toggle(i)}
            >
              {item.q}
            </button>
            <div
              id={`faq-panel-${i}`}
              className="faq-answer"
              role="region"
              aria-hidden={openIndex !== i}
            >
              <p>{item.a}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

  const FranchiseForm = ({refProp}) => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
      name: '',
      phone: '',
      email: '',
      city: '',
      message: '',
      agreed: false
    });
  
    const [submitting, setSubmitting] = useState(false);
    const [popupMessage, setPopupMessage] = useState(null);
  
    const handleChange = (e) => {
      const { name, value, type, checked } = e.target;
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    };
  
    const handleSubmit = async (e) => {
      e.preventDefault();
  
      // Basic validation
      const { name, phone, email, city, agreed } = formData;
      if (!name || !phone || !email || !city || !agreed) {
        setPopupMessage("Please fill all required fields and accept the privacy policy.");
        return;
      }
  
      setSubmitting(true);
      setPopupMessage(null);
  
      try {
        const response = await api.post('/franchiseRequest', {
          name: formData.name,
          phoneNumber: formData.phone,
          email: formData.email,
          message: formData.message,
          city: formData.city
        });
  
        if (response.data?.status?.statusCode === 'code-200') {
          // Redirect to Thank You page on success with state
          navigate('/franchise/thank-you', { 
            state: { fromFormSubmission: true },
            replace: true 
          });
        } else {
          setPopupMessage("Could not process your request. Please try again.");
          setTimeout(() => setPopupMessage(null), 3000);
        }
      } catch (error) {
        console.error(error);
        setPopupMessage("Something went wrong. Please try again later.");
        setTimeout(() => setPopupMessage(null), 3000);
      } finally {
        setSubmitting(false);
      }
    };

  return(
    <section className="franchise-form-section" ref={refProp} id='franchise-form'>
      <h2 className="form-title">Submit Application for NeatnClean Franchise</h2>
      <p className="form-subtitle">Join India's fastest growing laundry franchise network. Fill out the form below and our team will get back to you within 24 hours.</p>
      <form className="franchise-form" onSubmit={handleSubmit}>
        <input
          type="text"
          placeholder="Name*"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
        />
        <input
          type="tel"
          placeholder="Phone Number*"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          required
        />
        <input
          type="email"
          placeholder="Email ID*"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
        />
        <input
          type="text"
          placeholder="City*"
          name="city"
          value={formData.city}
          onChange={handleChange}
          required
        />

        <textarea
          placeholder="Write your message or questions here..."
          name="message"
          value={formData.message}
          onChange={handleChange}
          rows={4}
        ></textarea>

        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}>
          <input
            type="checkbox"
            id="privacy"
            name="agreed"
            checked={formData.agreed}
            onChange={handleChange}
            style={{ 
              margin: 0,
              padding: 0,
              marginRight: 8,
              width: '16px',
              height: '16px',
              appearance: 'checkbox', // ensures it's still a checkbox
              verticalAlign: 'middle',}}
          />
          <label htmlFor="privacy" style={{ display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
            I agree to the
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ marginLeft: '4px' }}>
              Privacy Policy
            </a>
          </label>
        </Box>

        <button type="submit" disabled={submitting}>
          {submitting ? 'Submitting...' : 'Submit Application'}
        </button>
      </form>

      {popupMessage && <div className="popup-message">{popupMessage}</div>}
    </section>
  );
}


const timelineData = [
  {
    icon: <FaHandshake />,
    title: 'Modern and Professional Interiors',
    desc: 'Sleek, professional, and welcoming spaces designed for efficiency and trust.'
  },
  {
    icon: <FaCheckCircle />,
    title: 'Business Model',
    desc: 'Low-cost, high-profit franchise with operational efficiency.'
  },
  {
    icon: <FaTools />,
    title: 'Support and Training',
    desc: 'Premium products, advanced equipment, and expert training provided.'
  },
  {
    icon: <FaUsersCog />,
    title: 'Service Experience',
    desc: 'Eco-friendly, high-quality cleaning for a superior experience.'
  },
  {
    icon: <FaMoneyBillWave />,
    title: 'Business Opportunity',
    desc: 'Join India’s top dry-cleaning brand with low risk and high returns.'
  }
];

function FranchiseTimelineFeature() {
  return (
    <section className="timeline-container">
      <h2>What NeatnClean brings to the table</h2>
      <div className="timeline">
        {timelineData.map((item, index) => (
          <div key={index} className={`timeline-item ${index % 2 === 0 ? 'left' : 'right'}`}>
            <div className="timeline-icon">{item.icon}</div>
            <div className="timeline-content">
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}